/** Переключатель-тумблер. Используется для уведомлений врачу и флагов доступа. */

export function Toggle({
  checked,
  onChange,
  disabled,
  title,
}: {
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      onClick={onChange}
      disabled={disabled}
      title={title}
      role="switch"
      aria-checked={checked}
      className="relative h-5 w-9 flex-shrink-0 rounded-full transition-colors disabled:opacity-40"
      style={{ background: checked ? 'var(--otc-accent)' : 'var(--otc-line)' }}
    >
      <span
        className="absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all"
        style={{ left: checked ? 18 : 2 }}
      />
    </button>
  );
}

/**
 * Настройка-переключатель с подписью и пояснением.
 *
 * Вместо системного квадратика с галочкой: у флажка не видно, включён он или
 * выключен, без пристального взгляда — а тут состояние читается цветом и
 * положением ползунка. Вся плашка кликабельна, попасть по ней легче.
 */
export function ToggleSetting({
  checked,
  onChange,
  label,
  hint,
  accent,
  disabled = false,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  hint?: string;
  /** Цвет включённого состояния. По умолчанию акцентный. */
  accent?: string;
  /** Настройка сейчас не действует: видна, но не нажимается. */
  disabled?: boolean;
}) {
  const activeColor = accent ?? 'var(--otc-accent)';

  return (
    <button
      onClick={() => onChange(!checked)}
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      className="flex w-full items-start gap-3 rounded-xl border px-3.5 py-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-45"
      style={{
        borderColor: checked ? activeColor : 'var(--otc-line)',
        background: checked ? `color-mix(in srgb, ${activeColor} 8%, transparent)` : 'var(--otc-surface)',
      }}
    >
      <span
        className="relative mt-0.5 h-5 w-9 flex-shrink-0 rounded-full transition-colors"
        style={{ background: checked ? activeColor : 'var(--otc-line)' }}
      >
        <span
          className="absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-all"
          style={{ left: checked ? 18 : 2 }}
        />
      </span>

      <span className="min-w-0">
        <span className="block text-sm font-medium text-ink">{label}</span>
        {hint && <span className="mt-0.5 block text-xs text-ink2">{hint}</span>}
      </span>
    </button>
  );
}

/** Компактное подтверждение «Да / ✕» для строк таблицы. */
export function TinyConfirm({ onConfirm, onCancel }: { onConfirm: () => void; onCancel: () => void }) {
  return (
    <span className="flex items-center gap-1 whitespace-nowrap">
      <button
        onClick={onConfirm}
        className="rounded px-1.5 py-0.5 text-xs text-white"
        style={{ background: 'var(--otc-coral)' }}
      >
        Да
      </button>
      <button
        onClick={onCancel}
        className="rounded border px-1.5 py-0.5 text-xs text-ink2"
        style={{ borderColor: 'var(--otc-line)' }}
      >
        ✕
      </button>
    </span>
  );
}
